import React from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import ArrowRightAltIcon from '@material-ui/icons/ArrowRightAlt';

import { fractionToPercent } from 'helpers/helpers';
import Message, { MessageVariant } from 'components/Message/Message';

import { Comparison } from 'stores/account/overviewStore/overviewStore';
import useStyles from './currentPortfolioStyles';
import useOtherStyles from './otherStyles';
import { EdwardColors } from 'enums';
import { RebalancingAction } from 'pages/Accounts/Detail/Overview/Overview';

interface Props {
  bucketType: string;
  comparison: Comparison;
  automaticRebalancing?: boolean;
  oneTimeRebalance?: boolean;
  rebalancingTimeTest?: boolean;
  showRebalancing: boolean;
  showInstruments: boolean;
  portfolioUpdate?: boolean;
}

const PortfolioTableComparison: React.FunctionComponent<Props> = ({
  bucketType,
  comparison,
  automaticRebalancing,
  oneTimeRebalance,
  rebalancingTimeTest,
  showRebalancing,
  showInstruments,
  portfolioUpdate
}) => {
  const { t } = useTranslation();
  const cssClasses = useStyles();
  const otherClasses = useOtherStyles();

  let rebalancing = RebalancingAction.NONE;

  if (automaticRebalancing === true) {
    if (oneTimeRebalance === true) {
      rebalancing = RebalancingAction.AUTO_ONCE;
    } else {
      rebalancing = !rebalancingTimeTest ? RebalancingAction.AUTO_STRICT : RebalancingAction.AUTO;
    }
  } else if (oneTimeRebalance === true) {
    rebalancing = RebalancingAction.ONCE;
  }

  return (
    <>
      {showRebalancing && <Message
        id={`overview_rebalancing_${bucketType}`}
        message={t(`accountDetail_overview_rebalancing_${rebalancing}`)}
        variant={MessageVariant.ACTION}
        permanent
      />}
      {showInstruments && (
        <Table className={`${cssClasses.root} planTable comparisonTable`}>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell />
              <TableCell className={`${otherClasses.shareTitle} categoryShare currentShare`}>
                {t('accountPlan_currentShare')}
              </TableCell>
              <TableCell />
              <TableCell className={`${otherClasses.shareTitle} categoryShare`}>
                {t(portfolioUpdate ? 'accountPlan_recommendedShare' : 'accountPlan_targetShare')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {comparison.assetCategories.map((assetCategory, catKey) => {
              return (
                <React.Fragment key={assetCategory.code}>
                  <TableRow
                    key={assetCategory.code}
                    className={`${otherClasses.category} ${assetCategory.code} category`}
                  >
                    <TableCell />
                    <TableCell scope="row" className="categoryTitle">
                      <strong>{t(assetCategory.title)}</strong>
                    </TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell align="right" className="categoryShare">
                      <strong>{fractionToPercent(assetCategory.proposedShare)}</strong>
                    </TableCell>
                  </TableRow>

                  {assetCategory.assetClasses.map((row, key) => (
                    <React.Fragment key={`${row.code}_${key}`}>
                      {row.assets &&
                        row.assets.map((asset, i) => (
                          <React.Fragment key={asset.code}>
                            <TableRow className="_assetRow">
                              <TableCell />
                              <TableCell scope="row" className={'titleCell'}>
                                <strong>{asset.title}</strong>
                              </TableCell>
                              <TableCell scope="row" className="currentShare">
                                {fractionToPercent(asset.share)}
                              </TableCell>
                              <TableCell>
                                {asset.share !== asset.proposedShare && <ArrowRightAltIcon fontSize="large" />}
                              </TableCell>
                              <TableCell
                                scope="row"
                                style={
                                  asset.share !== asset.proposedShare &&
                                  ([RebalancingAction.AUTO, RebalancingAction.ONCE].includes(rebalancing) ||
                                    asset.proposedShare === 0)
                                    ? { color: EdwardColors.WARNING }
                                    : undefined
                                }
                                className="proposedShare"
                              >
                                {fractionToPercent(asset.proposedShare)}
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        ))}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      )}
    </>
  );
};

export default observer(PortfolioTableComparison);
